import { Tooltip } from "antd";
import clsx from "clsx";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "./INA.scss";

export type INAProps = {
  image?: string;
  name: string;
  additional?: string;
  additional2?: string;
  className?: string;
};

const getInitial = (name: string) => {
  const trimmed = name.trim();

  if (!trimmed) {
    return "?";
  }

  return trimmed.charAt(0).toUpperCase();
};

type INATextProps = {
  children: ReactNode;
  title?: string;
  className?: string;
};

const INAText = memo(({ children, title, className }: INATextProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    setIsTruncated(element.scrollWidth > element.clientWidth);
  }, []);

  useEffect(() => {
    checkTruncation();

    const element = textRef.current;

    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    return () => observer.disconnect();
  }, [checkTruncation, children, title]);

  const tooltipTitle = title;
  const showTooltip = Boolean(tooltipTitle) && isTruncated;

  const content = (
    <p ref={textRef} className={clsx("tableINAText", className)}>
      {children}
    </p>
  );

  if (!showTooltip || !tooltipTitle) {
    return content;
  }

  return (
    <Tooltip title={tooltipTitle} mouseEnterDelay={0.35}>
      {content}
    </Tooltip>
  );
});

INAText.displayName = "INAText";

export const INA = memo(
  ({ image, name, additional, className, additional2 }: INAProps) => {
    const initial = getInitial(name);

    return (
      <div className={clsx("tableINA", className)}>
        <div className="tableINAImageWrap">
          {image ? (
            <img className="tableINAImage" src={image} />
          ) : (
            <span className="tableINAImageFallback" aria-hidden>
              {initial}
            </span>
          )}
        </div>

        <div className="tableINAContent">
          <INAText title={name} className="tableINAName">
            {name}
          </INAText>
          {additional ? (
            <INAText title={additional} className="tableINAAdditional">
              {additional}
            </INAText>
          ) : null}
          {additional2 ? (
            <INAText title={additional2} className="tableINAAdditional">
              {additional2}
            </INAText>
          ) : null}
        </div>
      </div>
    );
  },
);

INA.displayName = "INA";
